import { spotifyService } from './spotify';
import { aiService, PlaylistData, SearchResult } from './ai';
import { Playlist } from '../models';
// import { playlistAgentTools } from './playlistAgentSchema';
import { openaiService, ChatMessage } from './openaiService';

// Command parsing result types
export type CommandResult = 
  | { function: 'listPlaylists' }
  | { function: 'getPlaylistDetails', playlistId: string | null }
  | { function: 'createPlaylist', name: string | null, description: string, isPublic: boolean }
  | { function: 'addTracksToPlaylist', playlistId: string | null, trackUris: string[] }
  | { function: 'generatePlaylist', description: string | null, options: any }
  | { function: 'searchInternet', query: string | null };

// Result types
export type AgentResult = 
  | { type: 'playlists', data: Playlist[] }
  | { type: 'playlistDetails', data: Playlist }
  | { type: 'playlistCreated', data: Playlist & { 
      tracksFound?: number;
      tracksNotFound?: string[];
      totalTracksRequested?: number;
    }}
  | { type: 'tracksAdded', data: Playlist }
  | { type: 'playlistGenerated', data: PlaylistData }
  | { type: 'searchResult', data: SearchResult }
  | { type: 'displayedTracks', data: { tracks: any[], title?: string } }
  | { type: 'displayedPlaylist', data: { playlist: any } };

// Function execution history for tracking tool calls
export interface FunctionCall {
  function: string;
  parameters: Record<string, any>;
  result?: any;
  timestamp: Date;
}

export class SearchAgentService {
  // Enable logging for debugging - set to false in production
  private DEBUG = process.env.NODE_ENV !== 'production';
  
  // Track function call history
  private functionCallHistory: FunctionCall[] = [];
  
  // Logger function
  private log(message: string, data?: any): void {
    if (this.DEBUG) {
      console.log(`[SearchAgentService] ${message}`);
      // Only log data if it's not sensitive
      if (data && !this.isSensitiveData(data)) {
        console.log(data);
      }
    }
  }
  
  // Helper to check if data might contain sensitive information
  private isSensitiveData(data: any): boolean {
    // Check if the data might contain API keys or other sensitive info
    if (typeof data === 'string') {
      return data.includes('sk-') || data.includes('apiKey') || data.includes('key');
    }
    
    if (typeof data === 'object' && data !== null) {
      return Object.keys(data).some(key => 
        key.toLowerCase().includes('key') || 
        key.toLowerCase().includes('token') || 
        key.toLowerCase().includes('secret') ||
        key.toLowerCase().includes('password')
      );
    }
    
    return false;
  }
  
  // Track conversation history
  private conversationHistory: ChatMessage[] = [
    {
      role: 'system',
      content: `You are a helpful music search assistant. 
      You can help users search for music on Spotify, find information about artists, albums, and tracks,
      and display search results in a user-friendly way.
      
      IMPORTANT: When displaying search results, ALWAYS use the displayTracks or displayPlaylist functions
      instead of returning raw JSON data. These functions will format the results in a user-friendly way.
      
      Examples:
      - When a user asks for songs by an artist, use searchSpotify to find the tracks, then use displayTracks to show them.
      - When a user asks about a playlist, use getPlaylistDetails to get the information, then use displayPlaylist to show it.
      - When a user asks for a list of tracks, use displayTracks with an appropriate title.
      
      For general responses that don't involve displaying tracks or playlists, use this format:
      {
        "response": "Your response text here"
      }
      
      Always ensure your entire response is valid JSON.`
    }
  ];
  
  /**
   * Get the function call history
   */
  getFunctionCallHistory(): FunctionCall[] {
    return [...this.functionCallHistory];
  }
  
  /**
   * Get the conversation history
   */
  getConversationHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }
  
  /**
   * Clear the function call history and conversation history
   */
  clearHistory(): void {
    this.functionCallHistory = [];
    this.conversationHistory = [this.conversationHistory[0]]; // Keep system message
  }
  
  /**
   * Add a function call to the history
   */
  private addFunctionCall(functionName: string, parameters: Record<string, any>, result?: any): void {
    this.functionCallHistory.push({
      function: functionName,
      parameters,
      result,
      timestamp: new Date()
    });
  }

  /**
   * Process a user command using OpenAI function calling
   */
  async processCommand(command: string, singleFunctionMode: boolean = false): Promise<AgentResult[]> {
    // Add user message to conversation history
    this.conversationHistory.push({
      role: 'user',
      content: command
    });
    
    const results: AgentResult[] = [];
    let isProcessing = true;
    
    while (isProcessing) {
      // Call OpenAI API with conversation history and function definitions
      // Use 'search' agent type to ensure we use the searchAgentTools
      const response = await openaiService.sendMessage(
        this.conversationHistory,
        singleFunctionMode ? 'auto' : 'auto', // In single function mode, we still use 'auto' but will break after one call
        'search' // Specify that we're using the search agent
      );
      
      const message = response.choices[0].message;
      
      // Add assistant message to conversation history
      this.conversationHistory.push({
        role: 'assistant',
        content: message.content || '',
        tool_calls: message.tool_calls
      });
      
      // Check if the model wants to call a function
      if (message.tool_calls && message.tool_calls.length > 0) {
        // Process each tool call
        for (const toolCall of message.tool_calls) {
          if (toolCall.type === 'function') {
            const functionName = toolCall.function.name;
            const functionArgs = JSON.parse(toolCall.function.arguments);
            
            // Execute the function
            const functionResult = await this.executeFunction(functionName, functionArgs);
            
            // Add function result to conversation history
            this.conversationHistory.push({
              role: 'tool',
              tool_call_id: toolCall.id,
              content: JSON.stringify(functionResult.data)
            });
            
            // Add to results
            results.push(functionResult);
          }
        }
        
        // If in single function mode, break after processing one round of function calls
        if (singleFunctionMode) {
          isProcessing = false;
        }
      } else {
        // No more functions to call
        isProcessing = false;
      }
      
      // Check if we're done
      if (response.choices[0].finish_reason === 'stop') {
        isProcessing = false;
      }
    }
    
    return results;
  }

  /**
   * Execute a function based on name and arguments
   */
  private async executeFunction(functionName: string, args: any): Promise<AgentResult> {
    switch (functionName) {
      case 'searchInternet':
        return this.searchInternet(args.query);

      case 'searchSpotify':
        return this.searchSpotify(args.query,args.type);

      case 'listPlaylists':
        return this.listPlaylists();
      
      case 'getPlaylistDetails':
        if (!args.playlistId) {
          throw new Error('Playlist ID is required');
        }
        return this.getPlaylistDetails(args.playlistId);
      
      case 'displayTracks':
        return this.displayTracks(args.tracks, args.title);
      
      case 'displayPlaylist':
        return this.displayPlaylist(args.playlist);
      
      default:
        throw new Error(`Unknown function: ${functionName}`);
    }
  }

  /**
   * Search the internet
   */
  async searchInternet(query: string): Promise<AgentResult> {
    try {
      // Fix 1: Correct array syntax with square brackets
      const messages: ChatMessage[] = [
        {
          role: 'system',
          content: 'You are a helpful assistant that can search the internet for information.'
        },
        {
          role: 'user',
          content: query
        }
      ];
      
      // Get the chat completion response
      const chatResponse = await openaiService.sendFunctionlessMessage(messages, 'sonar');
      
      // Transform to SearchResult format
      const result: SearchResult = {
        results: [chatResponse.choices[0]?.message?.content || "No results found"]
      };
      
      // Add to function call history
      this.addFunctionCall('searchInternet', {}, result);
      
      return {
        type: 'searchResult',
        data: result
      };
    } catch (error) {
      // Fix 3: Correct the function name in the error logging
      // It was incorrectly using 'listPlaylists' instead of 'searchInternet'
      this.addFunctionCall('searchInternet', {}, { error });
      throw error;
    }
  }

  /**
   * Search Spotify for tracks, artists, albums, or playlists
   */
  async searchSpotify(query: string, type: string = 'track'): Promise<AgentResult> {
    try {
      // Get the search results from Spotify
      const searchResults = await spotifyService.search(query, type);
      
      // Add to function call history
      this.addFunctionCall('searchSpotify', { query, type }, searchResults);
      
      // Return the actual search results
      return {
        type: 'searchResult',
        data: searchResults
      };
    } catch (error) {
      this.addFunctionCall('searchSpotify', { query, type }, { error });
      throw error;
    }
  }

  /**
   * List user's playlists
   */
  async listPlaylists(): Promise<AgentResult> {
    try {
      const playlists = await spotifyService.getUserPlaylists();
      
      // Add to function call history
      this.addFunctionCall('listPlaylists', {}, playlists);
      
      return {
        type: 'playlists',
        data: playlists
      };
    } catch (error) {
      // Add failed call to history
      this.addFunctionCall('listPlaylists', {}, { error });
      throw error;
    }
  }

  /**
   * Get details of a specific playlist
   */
  async getPlaylistDetails(playlistId: string): Promise<AgentResult> {
    try {
      const playlist = await spotifyService.getPlaylist(playlistId);
      
      // Add to function call history
      this.addFunctionCall('getPlaylistDetails', { playlistId }, playlist);
      
      return {
        type: 'playlistDetails',
        data: playlist
      };
    } catch (error) {
      // Add failed call to history
      this.addFunctionCall('getPlaylistDetails', { playlistId }, { error });
      throw error;
    }
  }

  /**
   * Format and display tracks in a user-friendly way
   */
  async displayTracks(tracks: any[], title?: string): Promise<AgentResult> {
    try {
      // Add to function call history
      this.addFunctionCall('displayTracks', { tracks, title }, { tracks, title });
      
      return {
        type: 'displayedTracks',
        data: { tracks, title }
      };
    } catch (error) {
      // Add failed call to history
      this.addFunctionCall('displayTracks', { tracks, title }, { error });
      throw error;
    }
  }

  /**
   * Format and display a playlist in a user-friendly way
   */
  async displayPlaylist(playlist: any): Promise<AgentResult> {
    try {
      // Add to function call history
      this.addFunctionCall('displayPlaylist', { playlist }, { playlist });
      
      return {
        type: 'displayedPlaylist',
        data: { playlist }
      };
    } catch (error) {
      // Add failed call to history
      this.addFunctionCall('displayPlaylist', { playlist }, { error });
      throw error;
    }
  }
  
}

// Create a singleton instance
export const searchAgentService = new SearchAgentService();
